/**
 * @file Firestore CRUD and search for challenges, with IndexedDB mirroring for offline use.
 *
 * The module exposes three search variants that look nearly identical — they exist for
 * subtly different call sites:
 *  - `searchChallenges(pack, search)`     — searches within one pack's challenges
 *  - `searchAllChallenges(search)`        — searches the entire challenges collection
 *  - `searchAllChallengesRemovePack(...)` — same as `searchAllChallenges` but reserved
 *    for the "add challenge to pack" admin flow; the `_pack` arg is currently unused
 *    (kept to preserve the call-site signature — do NOT extend this with new behaviour).
 *
 * Pagination: `getAllChallenges` and `getChallenges` return at most 9 documents per call.
 * Pass the last document's id (or the previous slice's `start` index) to advance.
 */

import {
  addDoc,
  updateDoc,
  getDoc,
  doc,
  onSnapshot,
  collection,
  query,
  startAfter,
  limit,
  arrayRemove,
  arrayUnion,
  deleteDoc
} from 'firebase/firestore'
import { db } from '@/main'
import type { Pack, Challenge } from '@/types/Types'
import { saveChallenge, loadChallenge } from '@/utils/offlineChallenge'
import { FIREBASE_READ_TIMEOUT_MS } from '@/types/Constants'

import { removeAccents } from './methods'

/**
 * Fetches a page of challenges by resolving the next 9 ids from `pack.challenges` starting
 * at `start`. Rejects when the pack has no challenges or `start` is past the end — caller
 * uses the rejection to stop paginating.
 */
export function getChallenges(pack: Pack, start: number = 0): Promise<Challenge[]> {
  return new Promise((resolve, reject) => {
    if (pack.challenges === undefined || pack.challenges.length <= start) {
      reject(new Error('Pack does not have challenges or has reached end of challenges'))
      return
    }
    // Here we slice to get only 10 challenges at the time
    const challengesSlice = pack.challenges.slice(start, start + 9)
    const promises = challengesSlice.map((challengeId: string) => getChallengeFromId(challengeId))

    Promise.all(promises)
      .then((fetchedChallenges) => resolve(fetchedChallenges))
      .catch((error: Error) => reject(error))
  })
}

/**
 * Cursor-paginated read of the global challenges collection, with per-document offline
 * mirroring. Pass the previous page's last challenge id as `start` to continue;
 * pass `limitResults: false` to fetch everything (used only when the admin needs the
 * full list, e.g. for bulk operations).
 */
export function getAllChallenges(
  start: string = '',
  limitResults: boolean = true
): Promise<Challenge[]> {
  return new Promise((resolve, reject) => {
    ;(async () => {
      const fetchedChallenges: Challenge[] = []
      let challengesQuery
      if (start) {
        const startDoc = doc(db, 'challenges', start)
        const startSnapshot = await getDoc(startDoc)
        if (limitResults) {
          challengesQuery = query(collection(db, 'challenges'), startAfter(startSnapshot), limit(9))
        } else {
          challengesQuery = query(collection(db, 'challenges'), startAfter(startSnapshot))
        }
      } else if (limitResults) {
        challengesQuery = query(collection(db, 'challenges'), limit(9))
      } else {
        challengesQuery = query(collection(db, 'challenges'))
      }
      const unsub = onSnapshot(
        challengesQuery,
        (querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const data = doc.data()
            const newChallenge: Challenge = {
              id: doc.id,
              title: data.title,
              titleEnglish: data.titleEnglish || '',
              description: data.description,
              descriptionEnglish: data.descriptionEnglish || '',
              category: data.category,
              selectProblemBefore: data.selectProblemBefore,
              timer: data.timer,
              packs: data.packs,
              tags: data.tags,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt
            }
            fetchedChallenges.push(newChallenge)
            // Save to local storage for offline use
            saveChallenge(newChallenge)
          })
          unsub()
          resolve(fetchedChallenges)
        },
        (error: Error) => {
          reject(error)
        }
      )
    })()
  })
}

/** Returns just the ids of every challenge — used by `Pregame.vue` to sample challenges without paying for full document loads. */
export function getChallengesIds(): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const unsub = onSnapshot(
      collection(db, 'challenges'),
      (querySnapshot) => {
        const ids: string[] = []
        querySnapshot.forEach((doc) => {
          ids.push(doc.id)
        })
        unsub()
        resolve(ids)
      },
      (error: Error) => {
        reject(error)
      }
    )
  })
}

/**
 * Accent-insensitive client-side search across one pack's challenges. Matches against
 * bilingual title/description and the category name. Resolves with the empty array if
 * the pack has no challenges.
 */
export function searchChallenges(pack: Pack, search: string): Promise<Challenge[]> {
  return new Promise((resolve) => {
    const challenges: Challenge[] = []

    if (pack.challenges === undefined) {
      resolve(challenges)
      return
    }

    pack.challenges.forEach((challengeId: string, index: number) => {
      getChallengeFromId(challengeId).then((challenge: Challenge) => {
        const title = removeAccents(challenge.title.toLowerCase())
        const titleEnglish = removeAccents(
          challenge.titleEnglish ? challenge.titleEnglish.toLowerCase() : ''
        )
        const description = removeAccents(challenge.description.toLowerCase())
        const descriptionEnglish = removeAccents(
          challenge.descriptionEnglish ? challenge.descriptionEnglish.toLowerCase() : ''
        )
        const category = removeAccents(challenge.category.toLowerCase())
        const searchClean = removeAccents(search.toLowerCase())

        if (
          title.includes(searchClean) ||
          titleEnglish.includes(searchClean) ||
          description.includes(searchClean) ||
          descriptionEnglish.includes(searchClean) ||
          category.includes(searchClean)
        ) {
          challenges.push(challenge)
        }

        if (pack.challenges === undefined) {
          resolve(challenges)
          return
        }
        if (index === pack.challenges.length - 1) resolve(challenges)
      })
    })
  })
}

/** Accent-insensitive client-side search over EVERY challenge document. Loads the full collection, then filters in-memory — admin-path only. */
export function searchAllChallenges(search: string): Promise<Challenge[]> {
  return new Promise((resolve, reject) => {
    const fetchedChallenges: Challenge[] = []

    const unsub = onSnapshot(
      collection(db, 'challenges'),
      (querySnapshot) => {
        querySnapshot.forEach((doc) => {
          const data = doc.data()
          const title = removeAccents(data.title.toLowerCase())
          const titleEnglish = removeAccents(
            data.titleEnglish ? data.titleEnglish.toLowerCase() : ''
          )
          const description = removeAccents(data.description.toLowerCase())
          const descriptionEnglish = removeAccents(
            data.descriptionEnglish ? data.descriptionEnglish.toLowerCase() : ''
          )
          const category = removeAccents(data.category.toLowerCase())
          const searchClean = removeAccents(search.toLowerCase())

          if (
            title.includes(searchClean) ||
            titleEnglish.includes(searchClean) ||
            description.includes(searchClean) ||
            descriptionEnglish.includes(searchClean) ||
            category.includes(searchClean)
          ) {
            const newChallenge: Challenge = {
              id: doc.id,
              title: data.title,
              titleEnglish: data.titleEnglish || '',
              description: data.description,
              descriptionEnglish: data.descriptionEnglish || '',
              category: data.category,
              selectProblemBefore: data.selectProblemBefore,
              timer: data.timer,
              packs: data.packs,
              tags: data.tags,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt
            }

            fetchedChallenges.push(newChallenge)
          }
        })
        unsub()
        resolve(fetchedChallenges)
      },
      (error: Error) => {
        reject(error)
      }
    )
  })
}

/**
 * Variant of `searchAllChallenges` reserved for the "add to pack" admin flow. The `_pack`
 * argument was intended to exclude challenges already in the target pack, but the filter
 * is not yet implemented — the function currently returns the same result as `searchAllChallenges`.
 * Kept as a separate export so the admin call-site can be migrated without touching player paths.
 */
export function searchAllChallengesRemovePack(search: string, _pack: Pack): Promise<Challenge[]> {
  return new Promise((resolve, reject) => {
    const fetchedChallenges: Challenge[] = []

    const unsub = onSnapshot(
      collection(db, 'challenges'),
      (querySnapshot) => {
        querySnapshot.forEach((doc) => {
          const data = doc.data()
          const title = removeAccents(data.title.toLowerCase())
          const titleEnglish = removeAccents(
            data.titleEnglish ? data.titleEnglish.toLowerCase() : ''
          )
          const description = removeAccents(data.description.toLowerCase())
          const descriptionEnglish = removeAccents(
            data.descriptionEnglish ? data.descriptionEnglish.toLowerCase() : ''
          )
          const category = removeAccents(data.category.toLowerCase())
          const searchClean = removeAccents(search.toLowerCase())

          if (
            title.includes(searchClean) ||
            titleEnglish.includes(searchClean) ||
            description.includes(searchClean) ||
            descriptionEnglish.includes(searchClean) ||
            category.includes(searchClean)
          ) {
            const newChallenge: Challenge = {
              id: doc.id,
              title: data.title,
              titleEnglish: data.titleEnglish || '',
              description: data.description,
              descriptionEnglish: data.descriptionEnglish || '',
              category: data.category,
              selectProblemBefore: data.selectProblemBefore,
              timer: data.timer,
              packs: data.packs,
              tags: data.tags,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt
            }

            fetchedChallenges.push(newChallenge)
          }
        })
        unsub()
        resolve(fetchedChallenges)
      },
      (error: Error) => {
        reject(error)
      }
    )
  })
}

/**
 * Resolves a single challenge by id with offline fallback. The Firestore listener mirrors
 * the document to IndexedDB on every emission; if the document is missing or the listener
 * errors, the cached copy is returned. Rejects only when no copy is available either
 * online or offline — the rejection message guides the user toward going online once.
 *
 * AUD-28: a `FIREBASE_READ_TIMEOUT_MS` deadline is applied so a never-emitting listener
 * (rules denied, App Check blocked, stalled network) falls back to the offline cache
 * instead of hanging the caller forever.
 */
export function getChallengeFromId(challengeId: string): Promise<Challenge> {
  return new Promise((resolve, reject) => {
    let settled = false
    let unsub: (() => void) | null = null
    let timer: ReturnType<typeof setTimeout> | null = null

    async function resolveOfflineOrReject() {
      const offlineChallenge = await loadChallenge(challengeId)
      if (offlineChallenge) {
        resolve(offlineChallenge)
      } else {
        reject(
          new Error(
            'Challenge not found online or offline. Please connect to the internet at least once to cache this challenge.'
          )
        )
      }
    }

    function settle(fn: () => void | Promise<void>) {
      if (settled) return
      settled = true
      if (timer !== null) clearTimeout(timer)
      if (unsub) unsub()
      void fn()
    }

    timer = setTimeout(() => settle(resolveOfflineOrReject), FIREBASE_READ_TIMEOUT_MS)

    unsub = onSnapshot(
      doc(db, 'challenges', challengeId),
      (doc) => {
        if (!doc.exists() || doc.data() === undefined) {
          settle(resolveOfflineOrReject)
          return
        }
        const data = doc.data()
        const fetchedChallenge: Challenge = {
          id: doc.id,
          title: data.title,
          titleEnglish: data.titleEnglish || '',
          description: data.description,
          descriptionEnglish: data.descriptionEnglish || '',
          category: data.category,
          selectProblemBefore: data.selectProblemBefore,
          timer: data.timer,
          packs: data.packs,
          tags: data.tags,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        }
        settle(async () => {
          await saveChallenge(fetchedChallenge)
          resolve(fetchedChallenge)
        })
      },
      () => settle(resolveOfflineOrReject)
    )
  })
}

/** Adds a pack id to the challenge's `packs` reverse-edge array (`arrayUnion`, idempotent). Pair with `addChallengeToPack` to keep both directions in sync. */
export function addPackToChallenge(challengeId: string, packId: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    updateDoc(doc(db, 'challenges', challengeId), { packs: arrayUnion(packId) })
      .then(() => resolve(true))
      .catch((error: Error) => reject(error))
  })
}

/** Removes a pack id from the challenge's `packs` reverse-edge array (`arrayRemove`). Used by `deletePack` to clean up dangling references. */
export function removePack(challengeId: string, packId: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    updateDoc(doc(db, 'challenges', challengeId), { packs: arrayRemove(packId) })
      .then(() => resolve(true))
      .catch((error: Error) => reject(error))
  })
}

/** Creates a new challenge document and resolves with its Firestore-generated id. */
export function addChallenge(challenge: Challenge): Promise<string> {
  return new Promise((resolve, reject) => {
    addDoc(collection(db, 'challenges'), challenge)
      .then((docRef) => resolve(docRef.id))
      .catch((error: Error) => reject(error))
  })
}

/**
 * Partial update — caller passes only the fields to overwrite. The cast to
 * `{ [key: string]: never }` is a workaround for Firestore's strict generic; treat the
 * `challenge` argument as an arbitrary subset of `Challenge` fields, not as a full document.
 */
export function updateChallenge(
  challengeId: string,
  challenge: { [key: string]: unknown }
): Promise<void> {
  return new Promise((resolve, reject) => {
    updateDoc(doc(db, 'challenges', challengeId), challenge as { [key: string]: never })
      .then(() => resolve())
      .catch((error: Error) => reject(error))
  })
}

/** Hard-deletes a challenge. Pack documents still referencing this id are NOT cleaned up — the admin caller is responsible. */
export function deleteChallenge(challengeId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    deleteDoc(doc(db, 'challenges', challengeId))
      .then(() => resolve())
      .catch((error: Error) => reject(error))
  })
}
